import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft, CalendarRange, Database, Sparkles, Loader2,
  MousePointerClick, Search as SearchIcon, BarChart3, Youtube,
  ChevronDown, ChevronRight, FileText, Code2, AlertTriangle,
  Download, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

/** Static catalogue of every metric/dimension the user can ask us to pull for
 *  each Google service. Grouped + with short copy so the picker stays scannable.
 *
 *  Groups marked `advanced: true` live under an "Advanced" expander so the
 *  default picker only shows the most-used (account-level) metrics. Everything
 *  here maps directly to a real Google API metric/dimension — see
 *  `backend/google_integrations.py` for the live mapping.
 *
 *  Sources:
 *    - GA4 Data API:   https://developers.google.com/analytics/devguides/reporting/data/v1/api-schema
 *    - Google Ads:     https://developers.google.com/google-ads/api/fields/v15/metrics
 *    - GSC:            https://developers.google.com/webmaster-tools/v1/searchanalytics/query
 *    - YT Analytics:   https://developers.google.com/youtube/analytics/metrics
 */
import { FIELD_CATALOG, SVC_LABEL, SITE_SPECIFIC_SERVICES } from "@/components/googleFeed/fieldCatalog";
import { ymToday, lastDayOfMonth, formatNumber, metricFormat, formatMetric, humanLabel } from "@/components/googleFeed/formatters";
import { KpiTile, BreakdownTable, ReportView } from "@/components/googleFeed/ReportView";

export default function ClientGoogleFeedPreviewPage() {
  const { clientId, service, resourceId } = useParams();
  const navigate = useNavigate();

  const decodedRid = useMemo(() => decodeURIComponent(resourceId || ""), [resourceId]);
  const catalog = FIELD_CATALOG[service];

  const [selected, setSelected] = useState(() => {
    // Pre-select the first 4 fields from common (non-advanced) groups by default
    // so the user has something useful out of the box.
    const out = {};
    (catalog?.groups || []).filter((g) => !g.advanced).forEach((g) => {
      g.fields.forEach((f, i) => {
        if (i < 2 && Object.keys(out).length < 4) out[f.key] = true;
      });
    });
    return out;
  });

  const monthDefault = ymToday();
  const [periodMonth, setPeriodMonth] = useState(monthDefault);
  const [startDate, setStartDate] = useState(`${monthDefault}-01`);
  const [endDate, setEndDate] = useState(lastDayOfMonth(monthDefault));
  const [useMonthPicker, setUseMonthPicker] = useState(true);

  const [running, setRunning] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [viewMode, setViewMode] = useState("report"); // "report" | "json"

  // Business unit picker — only used when the service is site-specific
  // (GA4 / GSC). Loaded once on mount.
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBuId, setSelectedBuId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const needsBu = SITE_SPECIFIC_SERVICES.has(service);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/business-units`)
      .then(({ data }) => {
        if (cancelled) return;
        setBusinessUnits(data || []);
        if ((data || []).length > 0 && !selectedBuId) {
          setSelectedBuId(data[0].business_unit_id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  const commonGroups = useMemo(
    () => (catalog?.groups || []).filter((g) => !g.advanced),
    [catalog],
  );
  const advancedGroups = useMemo(
    () => (catalog?.groups || []).filter((g) => g.advanced),
    [catalog],
  );
  const advancedSelectedCount = useMemo(() => {
    let n = 0;
    advancedGroups.forEach((g) => g.fields.forEach((f) => { if (selected[f.key]) n += 1; }));
    return n;
  }, [advancedGroups, selected]);

  // Picking a month in month-mode auto-fills start/end dates.
  const handleMonthChange = (ym) => {
    setPeriodMonth(ym);
    setStartDate(`${ym}-01`);
    setEndDate(lastDayOfMonth(ym));
  };
  // Switching back to month-mode resyncs the dates to the picked month.
  const switchToMonthMode = () => {
    setUseMonthPicker(true);
    setStartDate(`${periodMonth}-01`);
    setEndDate(lastDayOfMonth(periodMonth));
  };

  const toggleField = (k) => setSelected({ ...selected, [k]: !selected[k] });
  const selectedKeys = Object.keys(selected).filter((k) => selected[k]);

  const run = async () => {
    if (selectedKeys.length === 0) {
      toast.error("Tick at least one field to preview.");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/google-feeds/preview`,
        {
          service, resource_id: decodedRid,
          fields: selectedKeys,
          start_date: startDate, end_date: endDate,
        },
      );
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setRunning(false); }
  };

  const importToReport = async () => {
    if (selectedKeys.length === 0) {
      toast.error("Tick at least one field to import.");
      return;
    }
    if (needsBu && !selectedBuId) {
      toast.error("Pick a property — this Google service is site-specific.");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/google-feeds/import`,
        {
          service, resource_id: decodedRid,
          fields: selectedKeys,
          start_date: startDate, end_date: endDate,
          business_unit_id: needsBu ? selectedBuId : null,
          overwrite: true,
        },
      );
      setImportResult(data);
      if (data.imported) {
        toast.success(`Imported ${data.rows} rows into ${data.platform} (${data.period_month})`);
      } else {
        toast.warning(data.reason || "Nothing to import.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  if (!catalog) {
    return (
      <div className="p-8 text-sm text-zinc-500" data-testid="feed-preview-unknown">
        Unknown Google service: <code className="font-mono">{service}</code>
      </div>
    );
  }

  const Icon = catalog.icon;

  return (
    <div className="space-y-6" data-testid="feed-preview-page">
      <div>
        <Button variant="ghost" size="sm" className="rounded-sm -ml-2 mb-2"
          onClick={() => navigate(`/clients/${clientId}/google-feeds`)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back to Google Feeds
        </Button>
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-sm bg-[#002FA7] text-white grid place-items-center shrink-0">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="label-overline">Live data preview</div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight">
              {catalog.title} · {decodedRid}
            </h1>
            <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
              Choose which metrics + breakdowns you want pulled for this resource and the period
              to cover. We&apos;ll show the preview here and (Phase 2) write the result into the
              monthly report.
            </p>
          </div>
        </div>
      </div>

      {/* Date range selector */}
      <Card className="rounded-sm border-zinc-200 bg-zinc-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-8 w-8 grid place-items-center rounded-sm bg-[#002FA7] text-white">
              <CalendarRange className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="label-overline">Date range</div>
              <div className="text-xs text-zinc-500">Default is the current month.</div>
            </div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider">
              <button type="button"
                className={`px-2 py-1 rounded-sm border ${useMonthPicker ? "border-[#002FA7] text-[#002FA7] bg-white" : "border-zinc-200 text-zinc-500 bg-white"}`}
                onClick={switchToMonthMode}>By month</button>
              <button type="button"
                className={`px-2 py-1 rounded-sm border ${!useMonthPicker ? "border-[#002FA7] text-[#002FA7] bg-white" : "border-zinc-200 text-zinc-500 bg-white"}`}
                onClick={() => setUseMonthPicker(false)}>Custom</button>
            </div>
            {useMonthPicker ? (
              <Input type="month" value={periodMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="rounded-sm h-9 w-[200px] font-mono text-xs pr-2"
                data-testid="preview-month" />
            ) : (
              <>
                <Input type="date" value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="rounded-sm h-9 w-[180px] font-mono text-xs pr-2"
                  data-testid="preview-start" />
                <span className="text-zinc-400 text-xs">→</span>
                <Input type="date" value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="rounded-sm h-9 w-[180px] font-mono text-xs pr-2"
                  data-testid="preview-end" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Field selector */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-zinc-500" />
              <div className="label-overline">What data should we pull?</div>
              <span className="text-[10px] font-mono px-1.5 rounded-sm bg-[#002FA7] text-white">
                {selectedKeys.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {needsBu && (
                <select
                  value={selectedBuId}
                  onChange={(e) => setSelectedBuId(e.target.value)}
                  className="h-9 rounded-sm border border-zinc-200 bg-white px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                  data-testid="import-bu-select"
                >
                  {businessUnits.length === 0 && (
                    <option value="">No properties — add one first</option>
                  )}
                  {businessUnits.map((bu) => (
                    <option key={bu.business_unit_id} value={bu.business_unit_id}>
                      {bu.name}
                    </option>
                  ))}
                </select>
              )}
              <Button onClick={run} disabled={running || selectedKeys.length === 0}
                variant="outline"
                className="rounded-sm border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"
                data-testid="preview-run">
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                         : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                {running ? "Pulling…" : "Preview data"}
              </Button>
              <Button onClick={importToReport}
                disabled={importing || selectedKeys.length === 0 || (needsBu && !selectedBuId)}
                className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
                data-testid="import-run">
                {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                           : <Download className="h-3.5 w-3.5 mr-1.5" />}
                {importing ? "Importing…" : "Import to Report"}
              </Button>
            </div>
          </div>

          {importResult?.imported && (
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2"
              data-testid="import-success">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1 text-[12px]">
                <div className="font-semibold text-emerald-800">
                  Imported {importResult.rows} row{importResult.rows === 1 ? "" : "s"} into{" "}
                  <span className="font-mono">{importResult.platform}</span> for{" "}
                  <span className="font-mono">{importResult.period_month}</span>
                </div>
                <div className="text-emerald-700 mt-0.5">
                  {importResult.totals_count} totals · {importResult.breakdowns_count} breakdowns ·
                  available in Reports, Charts and Historical Stats.
                </div>
              </div>
            </div>
          )}

          {commonGroups.map((g) => (
            <div key={g.label}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                {g.label}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {g.fields.map((f) => (
                  <label key={f.key}
                    className={`flex items-center gap-2 p-2.5 rounded-sm border cursor-pointer hover:bg-zinc-50 transition-colors ${
                      selected[f.key]
                        ? "border-[#002FA7] bg-[#002FA7]/5"
                        : "border-zinc-200 bg-white"
                    }`}
                    data-testid={`preview-field-${f.key}`}
                  >
                    <Checkbox
                      checked={!!selected[f.key]}
                      onCheckedChange={() => toggleField(f.key)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">{f.desc}</div>
                      <div className="font-mono text-[10px] text-zinc-400">{f.key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}

          {advancedGroups.length > 0 && (
            <div className="pt-2 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-700 hover:text-[#002FA7] transition-colors"
                data-testid="preview-advanced-toggle"
              >
                {showAdvanced
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
                Advanced breakdowns
                <span className="text-[10px] font-mono px-1.5 rounded-sm bg-zinc-100 text-zinc-600 normal-case tracking-normal">
                  {advancedSelectedCount > 0
                    ? `${advancedSelectedCount} selected`
                    : `${advancedGroups.reduce((n, g) => n + g.fields.length, 0)} available`}
                </span>
              </button>
              {showAdvanced && (
                <div className="mt-3 space-y-5" data-testid="preview-advanced-panel">
                  {advancedGroups.map((g) => (
                    <div key={g.label}>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
                        {g.label}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {g.fields.map((f) => (
                          <label key={f.key}
                            className={`flex items-center gap-2 p-2.5 rounded-sm border cursor-pointer hover:bg-zinc-50 transition-colors ${
                              selected[f.key]
                                ? "border-[#002FA7] bg-[#002FA7]/5"
                                : "border-zinc-200 bg-white"
                            }`}
                            data-testid={`preview-field-${f.key}`}
                          >
                            <Checkbox
                              checked={!!selected[f.key]}
                              onCheckedChange={() => toggleField(f.key)}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm">{f.desc}</div>
                              <div className="font-mono text-[10px] text-zinc-400">{f.key}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview result */}
      {preview && (
        <Card className="rounded-sm border-zinc-200" data-testid="preview-result">
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div>
                <div className="label-overline">Preview result</div>
                <h3 className="font-display font-bold text-xl">
                  {preview.start_date} → {preview.end_date}
                </h3>
                <div className="text-[11px] text-zinc-500 font-mono">
                  {selectedKeys.length} field{selectedKeys.length === 1 ? "" : "s"} · {SVC_LABEL[service]}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-mono ${
                  preview.live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"
                }`}>{preview.live ? "Live data" : "No data returned"}</span>
                <div className="inline-flex rounded-sm border border-zinc-200 overflow-hidden">
                  <button type="button"
                    onClick={() => setViewMode("report")}
                    className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider flex items-center gap-1.5 ${
                      viewMode === "report"
                        ? "bg-[#002FA7] text-white"
                        : "bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                    data-testid="view-mode-report"
                  >
                    <FileText className="h-3 w-3" /> Report
                  </button>
                  <button type="button"
                    onClick={() => setViewMode("json")}
                    className={`px-2.5 py-1.5 text-[10px] uppercase tracking-wider flex items-center gap-1.5 border-l border-zinc-200 ${
                      viewMode === "json"
                        ? "bg-[#002FA7] text-white"
                        : "bg-white text-zinc-600 hover:bg-zinc-50"
                    }`}
                    data-testid="view-mode-json"
                  >
                    <Code2 className="h-3 w-3" /> JSON
                  </button>
                </div>
              </div>
            </div>

            {Array.isArray(preview.warnings) && preview.warnings.length > 0 && (
              <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
                data-testid="preview-warnings">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
                    {preview.warnings.length} warning{preview.warnings.length === 1 ? "" : "s"} from Google
                  </div>
                  <ul className="text-[11px] font-mono text-amber-800 space-y-0.5">
                    {preview.warnings.map((w, i) => (
                      <li key={`w-${i}`} className="break-words">· {w}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {viewMode === "report" ? (
              <ReportView service={service} data={preview.data} />
            ) : (
              <pre className="text-[11px] font-mono whitespace-pre-wrap bg-zinc-50 border border-zinc-200 p-3 rounded-sm overflow-x-auto max-h-[60vh]"
                data-testid="preview-json">
                {JSON.stringify(preview.data || {}, null, 2)}
              </pre>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
